import React from "react";
import PropTypes from "prop-types";
import {
  Segment,
  Header,
  Button,
  Icon,
  Table,
  Divider,
  Grid,
  Label,
  Tab,
} from "semantic-ui-react";

const formatSectionLabel = (sectionValue) => {
  const labelMap = {
    Graduate_Thesis: "Graduate Thesis",
    Undergraduate_Thesis: "Undergraduate Thesis",
  };
  return labelMap[sectionValue] || sectionValue;
};

const DocumentUsageBreakdown = ({
  reportData,
  formData,
  sectionLabels,
  onExportPDF,
  onExportCSV,
  filtersApplied,
}) => {
  // Sort sections to put "Undefined Section" last
  const sections = Object.entries(reportData?.data?.sections || {}).sort(
    (a, b) => {
      const [keyA] = a;
      const [keyB] = b;

      // If keyA is "Undefined Section", it should come after keyB
      if (keyA === "Undefined Section") return 1;
      // If keyB is "Undefined Section", it should come after keyA
      if (keyB === "Undefined Section") return -1;
      // Otherwise, maintain original order
      return 0;
    }
  );

  const [activeSection, setActiveSection] = React.useState(
    sections[0]?.[0] || null
  );

  if (!reportData || !filtersApplied) return null;

  const renderSectionContent = (sectionKey, sectionData) => {
    const demographics =
      reportData.data?.detailed_breakdown?.[sectionKey] || {};
    const hasData = Object.keys(demographics.by_user_type || {}).length > 0;

    if (!hasData) {
      return (
        <div style={{ padding: "2rem", textAlign: "center", color: "#999" }}>
          <Icon name="info circle" size="large" />
          <p>No detailed data available for this section.</p>
        </div>
      );
    }

    const totalTransactions = sectionData.checkouts + sectionData.returns;

    return (
      <div>
        {/* Statistics Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #1e7a5f 0%, #2d9876 100%)",
            border: "3px solid #f4b952",
            padding: "2rem",
            color: "white",
            borderRadius: "8px",
            marginBottom: "2rem",
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "2rem",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{ fontSize: "4em", fontWeight: "bold", lineHeight: "1" }}
            >
              {sectionData.checkouts}
            </div>
            <div
              style={{
                fontSize: "1.1em",
                opacity: 0.95,
                marginTop: "0.75rem",
                fontWeight: "500",
              }}
            >
              Checkouts
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div
              style={{ fontSize: "4em", fontWeight: "bold", lineHeight: "1" }}
            >
              {sectionData.returns}
            </div>
            <div
              style={{
                fontSize: "1.1em",
                opacity: 0.95,
                marginTop: "0.75rem",
                fontWeight: "500",
              }}
            >
              Returns
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div
              style={{ fontSize: "4em", fontWeight: "bold", lineHeight: "1" }}
            >
              {totalTransactions}
            </div>
            <div
              style={{
                fontSize: "1.1em",
                opacity: 0.95,
                marginTop: "0.75rem",
                fontWeight: "500",
              }}
            >
              Total Transactions
            </div>
          </div>
        </div>

        {/* User Type Summary Cards */}
        <Header as="h4" style={{ color: "#2c3e50", marginBottom: "1rem" }}>
          <Icon name="users" />
          User Breakdown
        </Header>

        <Grid columns={4} stackable style={{ marginBottom: "1.5rem" }}>
          {Object.entries(demographics.by_user_type || {}).map(
            ([userType, userData]) => {
              const userTotal = userData.checkouts + userData.returns;
              const percentage =
                totalTransactions > 0
                  ? ((userTotal / totalTransactions) * 100).toFixed(1)
                  : 0;

              return (
                <Grid.Column key={userType}>
                  <div
                    style={{
                      padding: "1rem",
                      backgroundColor: "white",
                      borderRadius: "8px",
                      border: "2px solid #f4b952",
                      textAlign: "center",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "1.8em",
                        fontWeight: "bold",
                        color: "#1e7a5f",
                        marginBottom: "0.25rem",
                      }}
                    >
                      {userTotal}
                    </div>
                    <div
                      style={{
                        fontSize: "0.9em",
                        fontWeight: "600",
                        color: "#2c3e50",
                        textTransform: "capitalize",
                      }}
                    >
                      {userType}
                    </div>
                    <Label
                      size="tiny"
                      style={{
                        marginTop: "0.5rem",
                        backgroundColor: "#e8f5e8",
                        color: "#1e7a5f",
                      }}
                    >
                      {percentage}%
                    </Label>
                  </div>
                </Grid.Column>
              );
            }
          )}
        </Grid>

        <Divider />

        {/* Detailed Program Tables */}
        {Object.entries(demographics.by_user_type || {}).map(
          ([userType, userData]) => {
            if (
              !userData.programs ||
              Object.keys(userData.programs).length === 0
            )
              return null;

            return (
              <div
                key={`${userType}-programs`}
                style={{ marginBottom: "2rem" }}
              >
                <Header
                  as="h5"
                  style={{
                    color: "#black",
                    marginBottom: "1rem",
                    paddingBottom: "0.5rem",
                    borderBottom: "2px solid #e8f5e8",
                  }}
                >
                  <Icon name="graduation cap" />
                  {userType} - Program Details
                </Header>

                <Table
                  celled
                  striped
                  style={{
                    backgroundColor: "white",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  <Table.Header>
                    <Table.Row style={{ backgroundColor: "#f8f9fa" }}>
                      <Table.HeaderCell style={{ fontWeight: "bold" }}>
                        Program
                      </Table.HeaderCell>
                      <Table.HeaderCell
                        textAlign="center"
                        style={{ fontWeight: "bold" }}
                      >
                        Checkouts
                      </Table.HeaderCell>
                      <Table.HeaderCell
                        textAlign="center"
                        style={{ fontWeight: "bold" }}
                      >
                        Returns
                      </Table.HeaderCell>
                      <Table.HeaderCell
                        textAlign="center"
                        style={{ fontWeight: "bold" }}
                      >
                        Total
                      </Table.HeaderCell>
                      {Object.values(userData.programs).some(
                        (p) =>
                          p.year_levels && Object.keys(p.year_levels).length > 0
                      ) && (
                        <Table.HeaderCell
                          textAlign="center"
                          style={{ fontWeight: "bold" }}
                        >
                          Year Level Details
                        </Table.HeaderCell>
                      )}
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {Object.entries(userData.programs).map(
                      ([program, progData]) => {
                        const progTotal = progData.checkouts + progData.returns;

                        return (
                          <Table.Row key={program}>
                            <Table.Cell>
                              <strong style={{ color: "#2c3e50" }}>
                                {program}
                              </strong>
                            </Table.Cell>
                            <Table.Cell textAlign="center">
                              <Label circular color="blue" size="small">
                                {progData.checkouts}
                              </Label>
                            </Table.Cell>
                            <Table.Cell textAlign="center">
                              <Label circular color="purple" size="small">
                                {progData.returns}
                              </Label>
                            </Table.Cell>
                            <Table.Cell textAlign="center">
                              <Label circular color="green" size="small">
                                <strong>{progTotal}</strong>
                              </Label>
                            </Table.Cell>

                            {Object.values(userData.programs).some(
                              (p) =>
                                p.year_levels &&
                                Object.keys(p.year_levels).length > 0
                            ) && (
                              <Table.Cell textAlign="center">
                                {progData.year_levels &&
                                Object.keys(progData.year_levels).length > 0 ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: "0.5rem",
                                      justifyContent: "center",
                                    }}
                                  >
                                    {Object.entries(progData.year_levels).map(
                                      ([yearLevel, yearData]) => (
                                        <Label
                                          key={yearLevel}
                                          size="small"
                                          style={{
                                            backgroundColor: "#fff3cd",
                                            color: "#856404",
                                            border: "1px solid #ffc107",
                                          }}
                                        >
                                          <strong>{yearLevel}</strong>
                                          <Label.Detail>
                                            {yearData.checkouts +
                                              yearData.returns}
                                          </Label.Detail>
                                        </Label>
                                      )
                                    )}
                                  </div>
                                ) : (
                                  <em
                                    style={{
                                      color: "#999",
                                      fontSize: "0.85em",
                                    }}
                                  >
                                    No year level data
                                  </em>
                                )}
                              </Table.Cell>
                            )}
                          </Table.Row>
                        );
                      }
                    )}
                  </Table.Body>
                </Table>
              </div>
            );
          }
        )}
      </div>
    );
  };

  const renderSummaryTable = () => {
    return (
      <Table celled striped style={{ borderRadius: "8px", overflow: "hidden" }}>
        <Table.Header>
          <Table.Row style={{ backgroundColor: "#2c3e50" }}>
            <Table.HeaderCell style={{ color: "#225745", fontWeight: "bold" }}>
              Library Section
            </Table.HeaderCell>
            <Table.HeaderCell
              textAlign="center"
              style={{ color: "#225745", fontWeight: "bold" }}
            >
              Checkouts
            </Table.HeaderCell>
            <Table.HeaderCell
              textAlign="center"
              style={{ color: "#225745", fontWeight: "bold" }}
            >
              Returns
            </Table.HeaderCell>
            <Table.HeaderCell
              textAlign="center"
              style={{ color: "#225745", fontWeight: "bold" }}
            >
              Total
            </Table.HeaderCell>
            <Table.HeaderCell
              textAlign="center"
              style={{ color: "#225745", fontWeight: "bold" }}
            >
              Usage %
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {Object.entries(reportData.data?.sections || {}).map(
            ([section, data]) => {
              const total = data.checkouts + data.returns;
              const grandTotal =
                (reportData.data?.totals?.checkouts || 0) +
                (reportData.data?.totals?.returns || 0);
              const percentage =
                grandTotal > 0 ? ((total / grandTotal) * 100).toFixed(1) : 0;

              return (
                <Table.Row key={section}>
                  <Table.Cell>
                    <strong>
                      {formatSectionLabel(sectionLabels[section] || section)}
                    </strong>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <Label circular color="blue">
                      {data.checkouts}
                    </Label>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <Label circular color="green">
                      {data.returns}
                    </Label>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <Label circular color="teal">
                      <strong>{total}</strong>
                    </Label>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <strong style={{ color: "#1e7a5f" }}>{percentage}%</strong>
                  </Table.Cell>
                </Table.Row>
              );
            }
          )}
        </Table.Body>

        <Table.Footer>
          <Table.Row style={{ backgroundColor: "#f8f9fa" }}>
            <Table.HeaderCell>
              <strong style={{ fontSize: "1.1em" }}>TOTAL</strong>
            </Table.HeaderCell>
            <Table.HeaderCell textAlign="center">
              <Label circular color="blue" size="large">
                <strong>{reportData.data?.totals?.checkouts || 0}</strong>
              </Label>
            </Table.HeaderCell>
            <Table.HeaderCell textAlign="center">
              <Label circular color="green" size="large">
                <strong>{reportData.data?.totals?.returns || 0}</strong>
              </Label>
            </Table.HeaderCell>
            <Table.HeaderCell textAlign="center">
              <Label circular color="teal" size="large">
                <strong>
                  {(reportData.data?.totals?.checkouts || 0) +
                    (reportData.data?.totals?.returns || 0)}
                </strong>
              </Label>
            </Table.HeaderCell>
            <Table.HeaderCell textAlign="center">
              <strong style={{ fontSize: "1.1em", color: "#1e7a5f" }}>
                100%
              </strong>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>
    );
  };

  const renderSummaryPane = () => (
    <Tab.Pane
      style={{ border: "none", boxShadow: "none", padding: "1.5rem 0" }}
    >
      {renderSummaryTable()}
    </Tab.Pane>
  );

  const renderDetailedPane = () => {
    const sections = Object.entries(reportData.data?.sections || {});

    return (
      <Tab.Pane style={{ border: "none", boxShadow: "none", padding: "0" }}>
        <div
          style={{
            backgroundColor: "#f8f9fa",
            padding: "1rem",
            borderRadius: "8px",
            marginBottom: "1rem",
            overflowX: "auto",
            overflowY: "visible",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              paddingBottom: "0.5rem",
              minWidth: "max-content",
            }}
          >
            {sections.map(([sectionKey, sectionData]) => {
              const totalTransactions =
                sectionData.checkouts + sectionData.returns;
              const isActive = activeSection === sectionKey;

              return (
                <button
                  key={sectionKey}
                  onClick={() => setActiveSection(sectionKey)}
                  style={{
                    padding: "1rem 1.75rem",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "0.5rem",
                    backgroundColor: isActive ? "#1e7a5f" : "white",
                    borderRadius: "10px",
                    border: `3px solid ${isActive ? "#f4b952" : "#d0d0d0"}`,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    minWidth: "150px",
                    boxShadow: isActive
                      ? "0 6px 12px rgba(30,122,95,0.4)"
                      : "0 3px 6px rgba(0,0,0,0.15)",
                    transform: isActive
                      ? "translateY(-3px) scale(1.02)"
                      : "translateY(0) scale(1)",
                    outline: "none",
                    fontFamily: "inherit",
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.borderColor = "#1e7a5f";
                      e.currentTarget.style.transform =
                        "translateY(-3px) scale(1.02)";
                      e.currentTarget.style.boxShadow =
                        "0 6px 12px rgba(30,122,95,0.25)";
                      e.currentTarget.style.backgroundColor = "#f0f8f5";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.borderColor = "#d0d0d0";
                      e.currentTarget.style.transform =
                        "translateY(0) scale(1)";
                      e.currentTarget.style.boxShadow =
                        "0 3px 6px rgba(0,0,0,0.15)";
                      e.currentTarget.style.backgroundColor = "white";
                    }
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = isActive
                      ? "translateY(-1px) scale(1)"
                      : "translateY(-1px) scale(0.98)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = isActive
                      ? "translateY(-3px) scale(1.02)"
                      : "translateY(-3px) scale(1.02)";
                  }}
                >
                  <span
                    style={{
                      fontSize: "1em",
                      fontWeight: "700",
                      color: isActive ? "white" : "#2c3e50",
                      textAlign: "center",
                      letterSpacing: "0.3px",
                    }}
                  >
                    {formatSectionLabel(
                      sectionLabels[sectionKey] || sectionKey
                    )}
                  </span>
                  <Label
                    circular
                    size="medium"
                    style={{
                      backgroundColor: isActive ? "white" : "#1e7a5f",
                      color: isActive ? "#1e7a5f" : "white",
                      fontWeight: "bold",
                      padding: "0.5em 0.75em",
                    }}
                  >
                    {totalTransactions}
                  </Label>
                </button>
              );
            })}
          </div>
        </div>

        {/* Active Section Content */}
        <div style={{ padding: "1.5rem 0" }}>
          {activeSection &&
            renderSectionContent(
              activeSection,
              sections.find(([key]) => key === activeSection)?.[1]
            )}
        </div>
      </Tab.Pane>
    );
  };

  const panes = [
    {
      menuItem: {
        key: "summary",
        content: (
          <span
            style={{
              fontSize: "1.5em",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <Icon name="table" />
            Usage Summary by Section
          </span>
        ),
      },
      render: renderSummaryPane,
    },
    {
      menuItem: {
        key: "detailed",
        content: (
          <span
            style={{
              fontSize: "1.5em",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <Icon name="list alternate outline" />
            Detailed User Breakdown by Section
          </span>
        ),
      },
      render: renderDetailedPane,
    },
  ];

  return (
    <Segment style={{ border: "none", boxShadow: "none", padding: 0 }}>
      <div
        style={{
          backgroundColor: "white",
          padding: "2rem",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1.5rem",
          }}
        >
          <Header as="h2" style={{ margin: 0 }}>
            <Icon name="chart bar" color="green" />
            <Header.Content>
              Detailed Usage Breakdown
              <Header.Subheader
                style={{ marginTop: "0.5rem", fontSize: "14px" }}
              >
                Filtered Results for {reportData.period}
              </Header.Subheader>
            </Header.Content>
          </Header>

          {/* Export Buttons */}
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Button
              color="red"
              icon
              labelPosition="left"
              onClick={onExportPDF}
              size="small"
            >
              <Icon name="file pdf outline" />
              Export PDF
            </Button>
            <Button
              color="green"
              icon
              labelPosition="left"
              onClick={onExportCSV}
              size="small"
            >
              <Icon name="file excel outline" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Summary Statistics Cards */}
        <Grid columns={3} stackable style={{ marginBottom: "2rem" }}>
          <Grid.Column>
            <div
              style={{
                padding: "1.5rem",
                backgroundColor: "#e3f2fd",
                borderRadius: "8px",
                border: "2px solid #2196f3",
                display: "flex",
                alignItems: "center",
                gap: "4rem",
              }}
            >
              <Icon
                name="arrow up"
                size="huge"
                color="blue"
                style={{ marginLeft: "39px" }}
              />
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: "2.5em",
                    fontWeight: "bold",
                    color: "#1976d2",
                  }}
                >
                  {reportData.data?.totals?.checkouts || 0}
                </div>
                <div
                  style={{
                    fontSize: "1em",
                    fontWeight: "600",
                    color: "#424242",
                    marginTop: "5px",
                  }}
                >
                  Total Checkouts
                </div>
              </div>
            </div>
          </Grid.Column>
          <Grid.Column>
            <div
              style={{
                padding: "1.5rem",

                backgroundColor: "#f3e5f5",
                borderRadius: "8px",
                border: "2px solid #9c27b0",

                display: "flex",
                alignItems: "center",
                gap: "4rem",
              }}
            >
              <Icon
                name="arrow down"
                size="huge"
                color="purple"
                style={{ marginLeft: "39px" }}
              />
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: "2.5em",
                    fontWeight: "bold",

                    color: "#7b1fa2",
                  }}
                >
                  {reportData.data?.totals?.returns || 0}
                </div>
                <div
                  style={{
                    fontSize: "1em",
                    fontWeight: "600",
                    color: "#424242",
                    marginTop: "5px",
                  }}
                >
                  Total Returns
                </div>
              </div>
            </div>
          </Grid.Column>
          <Grid.Column>
            <div
              style={{
                padding: "1.5rem",
                backgroundColor: "#e8f5e9",
                borderRadius: "8px",
                border: "2px solid #4caf50",
                display: "flex",
                alignItems: "center",
                gap: "4rem",
              }}
            >
              <Icon
                name="exchange"
                size="huge"
                color="green"
                style={{ marginLeft: "39px" }}
              />
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: "2.5em",
                    fontWeight: "bold",
                    color: "#388e3c",
                  }}
                >
                  {(reportData.data?.totals?.checkouts || 0) +
                    (reportData.data?.totals?.returns || 0)}
                </div>
                <div
                  style={{
                    fontSize: "1em",
                    fontWeight: "600",
                    color: "#424242",
                    marginTop: "5px",
                  }}
                >
                  Total Transactions
                </div>
              </div>
            </div>
          </Grid.Column>
        </Grid>
        <Divider style={{ margin: "2rem 0" }} />

        {/* Tabs for Summary and Detailed Breakdown */}
        <div
          style={{
            backgroundColor: "#ffffff",
            borderRadius: "8px",
            padding: "0.5rem",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <Tab
            panes={panes}
            menu={{
              secondary: true,
              pointing: true,
              style: {
                marginBottom: "1.5rem",
                borderBottom: "2px solid #e0e0e0",
              },
            }}
          />
        </div>
      </div>
    </Segment>
  );
};

DocumentUsageBreakdown.propTypes = {
  reportData: PropTypes.shape({
    period: PropTypes.string,
    data: PropTypes.shape({
      sections: PropTypes.object,
      totals: PropTypes.shape({
        checkouts: PropTypes.number,
        returns: PropTypes.number,
      }),
      detailed_breakdown: PropTypes.object,
      debug_info: PropTypes.shape({
        loans_processed: PropTypes.number,
        loans_in_period: PropTypes.number,
        loans_with_user_details: PropTypes.number,
        loans_passed_filters: PropTypes.number,
      }),
      summary_message: PropTypes.string,
    }),
  }),
  formData: PropTypes.shape({
    sections: PropTypes.arrayOf(PropTypes.string),
    transactionType: PropTypes.string,
    userTypes: PropTypes.arrayOf(PropTypes.string),
    yearLevels: PropTypes.arrayOf(PropTypes.string),
    programs: PropTypes.arrayOf(PropTypes.string),
    departments: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  sectionLabels: PropTypes.objectOf(PropTypes.string).isRequired,
  onExportPDF: PropTypes.func.isRequired,
  onExportCSV: PropTypes.func.isRequired,
  filtersApplied: PropTypes.bool.isRequired,
};

DocumentUsageBreakdown.defaultProps = {
  reportData: null,
};

export default DocumentUsageBreakdown;
